import { useMemo, useState } from 'react';
import DramaCard from '@/components/feature/DramaCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { dramas } from '@/mocks/dramas';
import type { DramaStatus } from '@/types/content';

const PAGE_SIZE = 8;

type SortKey = 'latest' | 'oldest' | 'hot';
type StatusFilter = 'all' | DramaStatus;

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '最新更新' },
  { key: 'oldest', label: '最早更新' },
  { key: 'hot', label: '热度最高' },
];

const statusOptions: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: '全部状态' },
  { key: '连载', label: '连载中' },
  { key: '完结', label: '已完结' },
];

const chipClass = (active: boolean) =>
  `whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-label transition-colors cursor-pointer ${
    active
      ? 'bg-primary-500 text-background-50'
      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
  }`;

export default function DramaCategory() {
  const [sort, setSort] = useState<SortKey>('latest');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [genre, setGenre] = useState('all');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const genres = useMemo(
    () => Array.from(new Set(dramas.flatMap((drama) => drama.genres))),
    [],
  );

  const filtered = useMemo(() => {
    const list = dramas.filter((drama) => {
      const byStatus = status === 'all' || drama.status === status;
      const byGenre = genre === 'all' || drama.genres.includes(genre);
      return byStatus && byGenre;
    });
    return list.sort((a, b) => {
      if (sort === 'hot') return b.hot - a.hot;
      if (sort === 'oldest') return a.updateDate < b.updateDate ? -1 : 1;
      return a.updateDate < b.updateDate ? 1 : -1;
    });
  }, [sort, status, genre]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateFilter = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const resetFilters = () => {
    setSort('latest');
    setStatus('all');
    setGenre('all');
    setPage(1);
  };

  return (
    <div>
      <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex items-center justify-between gap-3 md:hidden">
          <span className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
            <i className="ri-filter-3-line text-primary-500" />
            筛选与排序
          </span>
          <button
            type="button"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700"
          >
            {filtersOpen ? '收起' : '展开'}
            <i className={filtersOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
          </button>
        </div>

        <div className={`${filtersOpen ? 'block' : 'hidden'} mt-4 space-y-4 md:mt-0 md:block md:space-y-4`}>
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <span className="shrink-0 text-sm text-foreground-500 md:w-20">更新时间</span>
            <div className="flex flex-wrap gap-2">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => updateFilter(() => setSort(option.key))}
                  className={chipClass(sort === option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <span className="shrink-0 text-sm text-foreground-500 md:w-20">剧集状态</span>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => updateFilter(() => setStatus(option.key))}
                  className={chipClass(status === option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 md:flex-row md:items-start">
            <span className="shrink-0 text-sm text-foreground-500 md:mt-1.5 md:w-20">类型标签</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => updateFilter(() => setGenre('all'))}
                className={chipClass(genre === 'all')}
              >
                全部类型
              </button>
              {genres.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => updateFilter(() => setGenre(item))}
                  className={chipClass(genre === item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-background-100 pt-3 text-sm text-foreground-500">
            <span>
              共找到 <strong className="text-primary-600">{filtered.length}</strong> 部剧集
            </span>
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-1 text-foreground-500 transition-colors hover:text-primary-500"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {paged.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
            <div className="mt-8">
              <Pagination current={safePage} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <EmptyState
            icon="ri-film-line"
            title="没有找到匹配的剧集"
            description="当前筛选条件下暂无内容，试试调整筛选条件或重置后重新浏览。"
            actionLabel="重置筛选条件"
            onAction={resetFilters}
          />
        )}
      </div>
    </div>
  );
}